import './style.css';
import { recomputeParking, scheduleSave, state } from './app';
import { onMapMouseDown, onMapMouseMove, onMapMouseUp } from './buildingInteraction';
import { redrawBuildingsOverlay } from './buildingRenderer';
import { initMapAsync, map, searchAddressAndFly, showMapLoading } from './mapView';
import { BICYCLE_ZONE_ID, isParkingZoneId, parkingZoneKeyFromId } from './parking/constants';
import {
  drawLockedSitePolygon,
  enterSiteEditMode,
  onMapClick,
  siteCancelEdit,
  siteClearAll,
  siteClearDraft,
  siteConfirm,
  siteUndoVertex,
} from './siteEditor';
import { wireDataTransferButtons } from './ui/dataTransfer';
import { removeBuilding, renderSidebar } from './ui/sidebar';

// Bootstrap: wires the fixed toolbar/status-bar buttons and the global
// delete-key handler once (mirrors index.html's original bottom-of-script
// wiring), then renders the sidebar and kicks off the (lazy) map load.
// Map-specific event handlers (click/mousedown/mousemove/mouseup) are wired
// after each successful map load, since a retry after a load failure
// creates a brand-new Leaflet map instance.

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

byId<HTMLButtonElement>('btn-site-edit')?.addEventListener('click', () => {
  if (state.ui.mode === 'site-edit') return;
  enterSiteEditMode();
});
byId<HTMLButtonElement>('btn-site-clear')?.addEventListener('click', siteClearAll);
wireDataTransferButtons(state);
byId<HTMLButtonElement>('address-search-btn')?.addEventListener('click', () => {
  searchAddressAndFly(byId<HTMLInputElement>('address-search-input')?.value ?? '');
});
byId<HTMLInputElement>('address-search-input')?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') searchAddressAndFly((e.target as HTMLInputElement).value);
});
byId<HTMLButtonElement>('site-undo-btn')?.addEventListener('click', siteUndoVertex);
byId<HTMLButtonElement>('site-clear-draft-btn')?.addEventListener('click', siteClearDraft);
byId<HTMLButtonElement>('site-confirm-btn')?.addEventListener('click', siteConfirm);
byId<HTMLButtonElement>('site-cancel-btn')?.addEventListener('click', siteCancelEdit);
byId<HTMLButtonElement>('map-retry-btn')?.addEventListener('click', () => {
  showMapLoading();
  startMap();
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Delete' && e.key !== 'Backspace') return;
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  const id = state.ui.selectedId;
  if (!id) return;
  e.preventDefault();
  if (id === BICYCLE_ZONE_ID) {
    state.bicycle.autoZone = true;
  } else if (isParkingZoneId(id)) {
    const m = state.parking.methods[parkingZoneKeyFromId(id)];
    if (m) m.zone = null;
  } else {
    removeBuilding(id);
    return;
  }
  state.ui.selectedId = null;
  recomputeParking();
  redrawBuildingsOverlay();
  renderSidebar();
  scheduleSave();
});

async function startMap(): Promise<void> {
  await initMapAsync();
  if (!map) return; // load failed; showMapLoadError() already ran inside initMapAsync
  map.on('click', onMapClick);
  map.on('mousedown', onMapMouseDown);
  map.on('mousemove', onMapMouseMove);
  map.on('mouseup', onMapMouseUp);
  if (state.site.locked && state.site.latlngs.length >= 3) {
    drawLockedSitePolygon();
  }
  redrawBuildingsOverlay();
}

renderSidebar();
startMap();
